import { FormSearchableSelectInput } from '@folio/components/forms/form-searchable-select-input';
import { useQuery } from '@tanstack/react-query';
import { sraControllerGetFacetQueryOptions } from 'korint-sdk';
import type { Field, SelectOption } from 'korint-sdk/react';
import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

type WatchedFieldNames = Partial<{
  brand: string;
  fiscalPower: string;
  energyCode: string;
  firstCirculationDate: string;
}>;

export const vehicleModelDefaultWatchedFieldNames: Required<WatchedFieldNames> = {
  brand: 'brand',
  fiscalPower: 'fiscalPower',
  energyCode: 'energyCode',
  firstCirculationDate: 'firstCirculationDate',
};

function getWatchedFieldName(
  propertyName: keyof WatchedFieldNames,
  watchedFieldNames?: WatchedFieldNames,
): string {
  if (watchedFieldNames?.[propertyName]) {
    return watchedFieldNames[propertyName];
  }
  return vehicleModelDefaultWatchedFieldNames[propertyName];
}

// This empty array provides referential equality for the default value of models
const emptyArray: Array<string> = [];

export function VehicleModelFormField({
  field,
  watchedFieldNames = vehicleModelDefaultWatchedFieldNames,
  disableAutoClear = false,
}: {
  field: Field;
  watchedFieldNames?: WatchedFieldNames;
  // When true, keep the current model even if the brand has no SRA models (e.g. a
  // free-text or SIV-sourced brand). The consumer owns cascade-resetting instead.
  disableAutoClear?: boolean;
}) {
  const form = useFormContext();

  const [brand] = form.watch([getWatchedFieldName('brand', watchedFieldNames)]) as [
    string | undefined,
  ];
  const currentModel = form.watch(field.name) as string | undefined;

  const { data: models = emptyArray, isLoading } = useQuery({
    ...sraControllerGetFacetQueryOptions({
      facetName: 'model',
      brand,
    }),
    enabled: !!brand,
    select: data => data.data,
  });

  useEffect(() => {
    if (!disableAutoClear && models.length === 0 && !isLoading) {
      form.setValue(field.name, null);
    }
  }, [models, isLoading]);

  // Keep the current value selectable/displayable even when it isn't in the SRA list
  // (SIV or manual entry), so switching tabs never hides what the broker already has.
  const optionValues =
    currentModel && !models.includes(currentModel) ? [currentModel, ...models] : models;

  return (
    <FormSearchableSelectInput
      field={{
        ...field,
        type: 'select',
        options: optionValues.map(value => ({ label: value, value })) as [
          SelectOption,
          ...SelectOption[],
        ],
        disabled: !brand,
      }}
      isLoading={isLoading}
    />
  );
}
